import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save } from "lucide-react";
import FileUploader from "@/components/FileUploader";

const TRUST_TYPES = ["Public Trust", "Educational Trust", "Charitable Trust"];
const STATUSES = ["Active", "Dormant"];

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
      {hint && <div className="mt-1 text-[11px] text-[#6b655b]">{hint}</div>}
    </label>
  );
}

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

export default function TrustDetailsPage() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/trust");
        setData(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: saved } = await api.put("/trust", data);
      setData(saved);
      toast.success("Trust details saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data)
    return (
      <div className="p-12 text-[#6b655b] font-serif text-xl" data-testid="trust-loading">
        Loading trust details…
      </div>
    );

  return (
    <div className="p-8 md:p-12 max-w-6xl mx-auto" data-testid="trust-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Master Record</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Trust Master Details</h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-xl">
            One-time master record of the trust — legal identity, registered office, and
            correspondence.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="trust-save-button"
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm transition-colors disabled:opacity-60"
        >
          <Save size={14} strokeWidth={1.5} />
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <Tabs defaultValue="basic" data-testid="trust-tabs">
        <TabsList className="bg-transparent p-0 border-b border-[#e5e0d8] rounded-none h-auto w-full justify-start gap-6">
          {[
            { v: "basic", l: "Basic Details" },
            { v: "registered", l: "Registered Office" },
            { v: "correspondence", l: "Correspondence Office" },
          ].map((t) => (
            <TabsTrigger
              key={t.v}
              value={t.v}
              data-testid={`tab-${t.v}`}
              className="data-[state=active]:bg-transparent data-[state=active]:text-[#234333] data-[state=active]:border-b-2 data-[state=active]:border-[#234333] rounded-none px-0 py-3 text-sm font-sans text-[#6b655b] shadow-none"
            >
              {t.l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="basic" className="pt-8">
          <div className="bg-white border border-[#e5e0d8] rounded-sm p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Trust Name (Full legal name)">
                <input
                  data-testid="trust-name-input"
                  className={inputCls}
                  value={data.trust_name}
                  onChange={(e) => set("trust_name", e.target.value)}
                />
              </Field>
              <Field label="Short Name / Abbreviation">
                <input
                  data-testid="trust-short-name-input"
                  className={inputCls}
                  value={data.short_name}
                  onChange={(e) => set("short_name", e.target.value)}
                />
              </Field>
              <Field label="Trust Registration Number">
                <input
                  data-testid="trust-reg-number-input"
                  className={inputCls}
                  value={data.registration_number}
                  onChange={(e) => set("registration_number", e.target.value)}
                />
              </Field>
              <Field label="Registration Date">
                <input
                  data-testid="trust-reg-date-input"
                  type="date"
                  className={inputCls}
                  value={data.registration_date || ""}
                  onChange={(e) => set("registration_date", e.target.value)}
                />
              </Field>
              <Field label="Registered under Act">
                <input
                  data-testid="trust-act-input"
                  className={inputCls}
                  placeholder="e.g., Maharashtra Public Trusts Act, 1950"
                  value={data.registered_under_act}
                  onChange={(e) => set("registered_under_act", e.target.value)}
                />
              </Field>
              <Field label="Trust Type">
                <select
                  data-testid="trust-type-select"
                  className={inputCls}
                  value={data.trust_type}
                  onChange={(e) => set("trust_type", e.target.value)}
                >
                  <option value="">— Select —</option>
                  {TRUST_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="PAN Number">
                <input
                  data-testid="trust-pan-input"
                  className={inputCls}
                  value={data.pan_number}
                  onChange={(e) => set("pan_number", e.target.value.toUpperCase())}
                />
              </Field>
              <Field label="TAN Number">
                <input
                  data-testid="trust-tan-input"
                  className={inputCls}
                  value={data.tan_number}
                  onChange={(e) => set("tan_number", e.target.value.toUpperCase())}
                />
              </Field>
              <Field label="GST Number">
                <input
                  data-testid="trust-gst-input"
                  className={inputCls}
                  value={data.gst_number}
                  onChange={(e) => set("gst_number", e.target.value.toUpperCase())}
                />
              </Field>
              <Field label="CIN (if any related entity)">
                <input
                  data-testid="trust-cin-input"
                  className={inputCls}
                  value={data.cin}
                  onChange={(e) => set("cin", e.target.value.toUpperCase())}
                />
              </Field>
              <Field label="Trust Status">
                <select
                  data-testid="trust-status-select"
                  className={inputCls}
                  value={data.trust_status}
                  onChange={(e) => set("trust_status", e.target.value)}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="mt-6 pt-6 border-t border-[#e5e0d8]">
              <Field label="Trust Logo (shown on the public website About → GSM section)">
                <FileUploader
                  kind="trust_logo"
                  label="Trust logo"
                  url={data.logo_url || ""}
                  testid="trust-logo"
                  onChange={(v) => {
                    setData((d) => ({ ...d, logo_url: v.url || "" }));
                  }}
                />
              </Field>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="registered" className="pt-8">
          <div className="bg-white border border-[#e5e0d8] rounded-sm p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Registered Address">
                <textarea
                  data-testid="trust-reg-address-input"
                  className={inputCls + " min-h-[88px] resize-y"}
                  value={data.reg_address}
                  onChange={(e) => set("reg_address", e.target.value)}
                />
              </Field>
              <div className="grid grid-cols-2 gap-6">
                <Field label="City">
                  <input
                    data-testid="trust-reg-city-input"
                    className={inputCls}
                    value={data.reg_city}
                    onChange={(e) => set("reg_city", e.target.value)}
                  />
                </Field>
                <Field label="District">
                  <input
                    data-testid="trust-reg-district-input"
                    className={inputCls}
                    value={data.reg_district}
                    onChange={(e) => set("reg_district", e.target.value)}
                  />
                </Field>
                <Field label="State">
                  <input
                    data-testid="trust-reg-state-input"
                    className={inputCls}
                    value={data.reg_state}
                    onChange={(e) => set("reg_state", e.target.value)}
                  />
                </Field>
                <Field label="PIN Code">
                  <input
                    data-testid="trust-reg-pincode-input"
                    className={inputCls}
                    value={data.reg_pincode}
                    onChange={(e) => set("reg_pincode", e.target.value)}
                  />
                </Field>
              </div>
              <Field label="Email ID">
                <input
                  data-testid="trust-reg-email-input"
                  type="email"
                  className={inputCls}
                  value={data.reg_email}
                  onChange={(e) => set("reg_email", e.target.value)}
                />
              </Field>
              <Field label="Website">
                <input
                  data-testid="trust-website-input"
                  className={inputCls}
                  placeholder="https://"
                  value={data.website}
                  onChange={(e) => set("website", e.target.value)}
                />
              </Field>
              <Field label="Contact Number">
                <input
                  data-testid="trust-reg-contact-input"
                  className={inputCls}
                  value={data.reg_contact}
                  onChange={(e) => set("reg_contact", e.target.value)}
                />
              </Field>
              <Field label="Alternate Contact Number">
                <input
                  data-testid="trust-reg-alt-contact-input"
                  className={inputCls}
                  value={data.reg_alt_contact}
                  onChange={(e) => set("reg_alt_contact", e.target.value)}
                />
              </Field>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="correspondence" className="pt-8">
          <div className="bg-white border border-[#e5e0d8] rounded-sm p-6 md:p-8">
            <label className="flex items-center gap-3 mb-6 cursor-pointer">
              <input
                data-testid="trust-corr-same-checkbox"
                type="checkbox"
                checked={data.corr_same_as_reg}
                onChange={(e) => set("corr_same_as_reg", e.target.checked)}
                className="accent-[#234333] w-4 h-4"
              />
              <span className="text-sm text-[#1c1a17]">
                Same as Registered Office
              </span>
            </label>

            {!data.corr_same_as_reg && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Address">
                  <textarea
                    data-testid="trust-corr-address-input"
                    className={inputCls + " min-h-[88px] resize-y"}
                    value={data.corr_address}
                    onChange={(e) => set("corr_address", e.target.value)}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-6">
                  <Field label="City">
                    <input
                      data-testid="trust-corr-city-input"
                      className={inputCls}
                      value={data.corr_city}
                      onChange={(e) => set("corr_city", e.target.value)}
                    />
                  </Field>
                  <Field label="State">
                    <input
                      data-testid="trust-corr-state-input"
                      className={inputCls}
                      value={data.corr_state}
                      onChange={(e) => set("corr_state", e.target.value)}
                    />
                  </Field>
                  <Field label="PIN Code">
                    <input
                      data-testid="trust-corr-pincode-input"
                      className={inputCls}
                      value={data.corr_pincode}
                      onChange={(e) => set("corr_pincode", e.target.value)}
                    />
                  </Field>
                  <Field label="Contact">
                    <input
                      data-testid="trust-corr-contact-input"
                      className={inputCls}
                      value={data.corr_contact}
                      onChange={(e) => set("corr_contact", e.target.value)}
                    />
                  </Field>
                </div>
                <Field label="Email">
                  <input
                    data-testid="trust-corr-email-input"
                    type="email"
                    className={inputCls}
                    value={data.corr_email}
                    onChange={(e) => set("corr_email", e.target.value)}
                  />
                </Field>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
